import { StrategyDetailsPageGetDocumentRecordQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import CheckBox from '@/components/basicComponents/checkbox';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { Investment } from '@/components/dealCatalog-page/types';
import { Loader } from '@/components/loaders/loader';
import { getManagerUpdateName } from '@/components/managerUpdates-page/utils';
import { getSettingsItemValue } from '@/components/settings-page';
import { FirmInfoType } from '@/components/settings-page/platformSettings/firmInfo';
import { ITemplateStylingSettings } from '@/components/settings-page/platformSettings/templatesSettings/types';
import { SYSTEM_SETTINGS_QUERY, TENANT_NAME_QUERY } from '@/components/settings-page/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { IDocumentRecordTemplate } from '@/shared/types/templates/documentRecordTemplate';
import { convertDocumentRecordFieldToFieldEditor } from '@/shared/ui/fieldList';
import { formattingDocumentRecordDataForDocx } from '@/shared/ui/templates/docx-templates/documentRecord-DOCXTemplate';
import { generateDocxTemplate } from '@/shared/ui/templates/docx-templates/utils';
import { DocumentRecordPDFTemplate } from '@/shared/ui/templates/pdf-templates/DocumentRecord-PDFTemplate';
import { themeVar } from '@/styles/theme';
import { gql, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY } from '../queries';

enum DownloadFormatEnum {
  PDF = 'PDF',
  DOCX = 'DOCX'
}

const INVESTMENT_QUERY = gql(`
  query DownloadPDFInvestment($id: String!) {
    Investment(id: $id) {
      id
      name
      description
    }
  }
`);

interface DownloadPDFProps {
  isOpen: boolean;
  updateId?: string | null;
  source: 'updateTab' | 'detailsTab';
  onClose: () => void;
}

const DownloadPDF = ({ isOpen, updateId, source, onClose }: DownloadPDFProps) => {
  const params = useParams();
  const { user } = useAuth();
  const theme = useTheme();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [includeAiSuggested, setIncludeAiSuggested] = useState(true);
  const [hasAIAnswers, setHasAIAnswers] = useState(false);
  const [isDocumentGenerating, setIsDocumentGenerating] = useState({ pdf: false, docx: false });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [firmInfoData, setFirmInfoData] = useState<FirmInfoType>({} as FirmInfoType);
  const [templateStylingData, setTemplateStylingData] = useState<ITemplateStylingSettings>({});
  const [templateData, setTemplateData] = useState<IDocumentRecordTemplate[]>([]);

  const { data: investmentData, loading: investmentLoading } = useQuery<{ Investment: Investment }>(INVESTMENT_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: { id: params?.strategyId },
    skip: !params?.strategyId,
    onError: (error) => {
      console.error(error);
    }
  });

  const { data: documentRecordData, loading: documentRecordLoading } = useQuery(STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !updateId,
    variables: {
      id: updateId
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    if (documentRecordLoading || !documentRecordData || investmentLoading || !investmentData) return;

    const { name, fields } = documentRecordData?.getDocumentRecord || {};

    const filteredManagerUpdateFields = fields?.filter(
      (field) => field.documentField.name !== 'Document Record Name' && field.documentField.name !== 'Document Record Publish Date'
    );

    if (source === 'updateTab') {
      const convertedUpdateTabFields = convertDocumentRecordFieldToFieldEditor(filteredManagerUpdateFields || []);
      setHasAIAnswers(!!fields?.some((field) => field.value === null && field.AI?.answers.length > 0));
      setTemplateData([
        {
          updateName: getManagerUpdateName(fields, name),
          investmentName: investmentData?.Investment?.name ?? '',
          investmentDescription: investmentData?.Investment?.description ?? '',
          fields: convertedUpdateTabFields
        }
      ]);
    } else {
      const descriptionField: StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields'][number] = fields?.[1]
        ? { ...fields?.[1], documentField: { ...fields?.[1].documentField, name: '' } }
        : null;
      const convertedDetailsTabFields = descriptionField ? convertDocumentRecordFieldToFieldEditor([descriptionField]) : [];
      setHasAIAnswers(!!descriptionField?.value === null && descriptionField?.AI?.answers.length > 0);
      setTemplateData([
        {
          updateName: 'Strategy Description',
          investmentName: investmentData?.Investment?.name ?? '',
          investmentDescription: investmentData?.Investment?.description ?? '',
          fields: convertedDetailsTabFields
        }
      ]);
    }
  }, [documentRecordLoading, documentRecordData, investmentLoading, investmentData]);

  const { data: tenantNameData, loading: tenantNameLoading } = useQuery(TENANT_NAME_QUERY, {
    variables: {
      id: user?.tenant?.id
    }
  });

  const { data: firmInfoQueryData, loading: firmInfoLoading } = useQuery(SYSTEM_SETTINGS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        key: 'firmInfo',
        type: 'config',
        allowNullTenantSettings: true
      }
    }
  });

  useEffect(() => {
    if (tenantNameLoading || !tenantNameData || firmInfoLoading || !firmInfoQueryData) return;

    const firmInfo = getSettingsItemValue(firmInfoQueryData?.systemSettings ?? [], 'firmInfo') || {};
    setFirmInfoData({ ...firmInfo, firmName: tenantNameData?.tenant?.name || '' });
  }, [tenantNameLoading, tenantNameData, firmInfoLoading, firmInfoQueryData]);

  const { loading: templateStylingLoading } = useQuery(SYSTEM_SETTINGS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        tenantId: user?.tenant?.id,
        type: 'customization'
      }
    },
    onCompleted: (data) => {
      const templateStyling = getSettingsItemValue(data?.systemSettings ?? [], 'templateStyling') || {};
      setTemplateStylingData(templateStyling);
    }
  });

  const changeCheckboxValue = () => {
    setIncludeAiSuggested((prev) => !prev);
  };

  const handleGeneratePdf = async () => {
    try {
      setIsDocumentGenerating((prev) => ({ ...prev, pdf: true }));
      setErrorMessage(null);

      const { pdf } = await import('@react-pdf/renderer');
      const pdfBlob = await pdf(
        <DocumentRecordPDFTemplate
          templateData={templateData}
          firmInfoData={firmInfoData}
          templateStylingData={templateStylingData}
          includeAiSuggested={includeAiSuggested}
          theme={theme}
        />
      ).toBlob();

      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsDocumentGenerating((prev) => ({ ...prev, pdf: false }));
    }
  };

  const handleGenerateDocx = async () => {
    try {
      setIsDocumentGenerating((prev) => ({ ...prev, docx: true }));
      setErrorMessage(null);

      if (!templateData.length) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      const investmentName = templateData?.[0]?.investmentName ?? '';
      const updateName = templateData?.[0]?.updateName ?? '';
      const formattedData = await formattingDocumentRecordDataForDocx(templateData, templateStylingData, includeAiSuggested, theme);

      const fileName = `${investmentName} - ${updateName}.docx`;
      await generateDocxTemplate(formattedData, firmInfoData, templateStylingData, fileName, theme);
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsDocumentGenerating((prev) => ({ ...prev, docx: false }));
    }
  };

  const handleDownload = async (selectedFormat: DownloadFormatEnum) => {
    const downloadHandlers = {
      [DownloadFormatEnum.PDF]: handleGeneratePdf,
      [DownloadFormatEnum.DOCX]: handleGenerateDocx
    };

    const handler = downloadHandlers[selectedFormat];
    if (handler) {
      await handler();
      onClose();
    }
  };

  return (
    <ModalWrappedContent isOpen={isOpen} onClose={onClose} title={'Download'} showCancelButton={false}>
      {investmentLoading || documentRecordLoading || tenantNameLoading || firmInfoLoading || templateStylingLoading ? (
        <Loader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <Container>
            {hasAIAnswers && (
              <CheckboxWrapper id={'input'} onClick={changeCheckboxValue}>
                <CheckBox id={'input'} isActive={includeAiSuggested} /> Include AI Suggested Content
              </CheckboxWrapper>
            )}
            <DownloadWrappper>
              <Button
                onClick={() => handleDownload(DownloadFormatEnum.PDF)}
                styleType={isDocumentGenerating.pdf ? 'disabled' : 'default'}
                isLoading={isDocumentGenerating.pdf}
                size="md"
              >
                Open PDF
              </Button>
              <Button
                onClick={() => handleDownload(DownloadFormatEnum.DOCX)}
                styleType={isDocumentGenerating.docx ? 'disabled' : 'default'}
                isLoading={isDocumentGenerating.docx}
                size="md"
              >
                Download DOCX
              </Button>
            </DownloadWrappper>
          </Container>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default DownloadPDF;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxWrapper = styled.div`
  cursor: pointer;
  display: flex;
  gap: 9px;
  margin-top: 20px;
  padding: 18px 0 30px 0;
  border-top: 1px solid ${themeVar('border.base')};
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-weight: 400;
`;

const DownloadWrappper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 30px;
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 20px;
`;
